// src/pages/Information/Components/UnlistedAssigneeCard.tsx
/**
 * Shown on the leader and facilitator detail pages when the userId in the URL has no record behind it.
 *
 * These are people who are doing real work, they own a case or are assigned to a course, but who were
 * never given a user in Identity, so `leaderService.getByUserId()` / `facilitatorService.getByUserId()`
 * both 404. Without this the detail page rendered a silent empty shell: "Unknown", no contact, no tabs
 * with anything in them, and no explanation.
 *
 * The create flow uses **`adminService.createUserAtId(userId, model)`**, which provisions the account at
 * the userId the existing assignment already references, so their work reconnects. That endpoint was
 * specced in `_deploy/_newEndPoints/unlisted-assignees.md` §2 and has since shipped.
 *
 * ⚠️ It is **feature-detected**, falling back to `createUser()` on an older SDK. `createUser` takes no id
 * and mints a fresh one, so on that path the original assignment stays dangling and the success message
 * says so instead of implying it was reconnected. Do not remove the fallback without raising the minimum
 * SDK version.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Code, Divider, Drawer, Group, Stack, Text, TextInput, Title,
} from '@mantine/core';
import { IconAlertTriangle, IconUserPlus } from '@tabler/icons-react';
import { Select } from '@/components';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useAdminService,
  useMemberService,
  useLeaderService,
  useLeaderTypeService,
  useFacilitatorService,
  useFacilitatorStatusService,
  type LeaderType,
  type FacilitatorStatus,
} from '@actschurch/ccms-sdk';
import { splitName } from '../unlistedAssignees';

type Kind = 'leader' | 'facilitator';

export interface UnlistedAssigneeCardProps {
  /** The userId from the route, carried on the assignment that referenced this person. */
  userId: string;
  /** Which list the person is missing from. Drives the copy, the role claim and the record created. */
  kind: Kind;
  /** Best name we have, usually passed through from the list page's assignment lookup. */
  fallbackName?: string | null;
  /** Called after the account and record are created, so the page can refetch. */
  onCreated?: () => void;
}

const COPY: Record<Kind, { noun: string; role: string; assignedTo: string }> = {
  leader: {
    noun: 'care support leader',
    role: 'ActsCareLeader',
    assignedTo: 'owns at least one care case',
  },
  facilitator: {
    noun: 'facilitator',
    role: 'ActsCareFacilitator',
    assignedTo: 'is assigned to course work',
  },
};

export function UnlistedAssigneeCard({
  userId,
  kind,
  fallbackName,
  onCreated,
}: UnlistedAssigneeCardProps) {
  const Admin = useAdminService();
  const Members = useMemberService();
  const Leaders = useLeaderService();
  const LeaderTypes = useLeaderTypeService();
  const Facilitators = useFacilitatorService();
  const FacilitatorStatuses = useFacilitatorStatusService();

  const copy = COPY[kind];
  const seeded = splitName(fallbackName ?? null);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [firstName, setFirstName] = useState(seeded.firstName);
  const [surname, setSurname] = useState(seeded.lastName);
  const [email, setEmail] = useState('');
  const [cellphone, setCellphone] = useState('');
  const [kindID, setKindID] = useState<string | null>(null);

  const [types, setTypes] = useState<LeaderType[]>([]);
  const [statuses, setStatuses] = useState<FacilitatorStatus[]>([]);

  // The record created alongside the account needs a real leader type / facilitator status, so the
  // lookup is fetched up front rather than defaulting to id 0, which may not exist.
  const loadLookups = useCallback(async () => {
    try {
      if (kind === 'leader') setTypes(await LeaderTypes.getAll());
      else setStatuses(await FacilitatorStatuses.getAll());
    } catch (e) {
      console.error('[UnlistedAssigneeCard] lookup fetch failed:', e);
    }
  }, [kind, LeaderTypes, FacilitatorStatuses]);

  useEffect(() => { if (open) loadLookups(); }, [open, loadLookups]);

  const lookupOptions = kind === 'leader'
    ? types.map((t) => ({ value: String(t.typeID), label: t.name }))
    : statuses.map((s) => ({ value: String(s.statusID), label: s.name }));

  const canSave = Boolean(email.trim() && firstName.trim() && kindID);

  const handleCreate = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        email: email.trim(),
        firstName: firstName.trim(),
        surname: surname.trim(),
        cellphone: cellphone.trim() || null,
        isMember: true,
        maritalStatusID: 0,
        roles: [copy.role],
      };

      // 1. The Identity account, created **at the userId the assignment already references**, which is
      // what reconnects their existing work. `createUserAtId` shipped in the SDK after this component
      // was written; it is feature-detected so an older SDK still falls back to minting a fresh id.
      const created = typeof Admin.createUserAtId === 'function'
        ? await Admin.createUserAtId(userId, payload)
        : await Admin.createUser(payload);

      const newUserId = created?.id?.trim() ?? userId;
      if (!newUserId) throw new Error('The account was created but no user id came back.');

      const reconnected = newUserId === userId;

      // 2. The memberID the leader/facilitator record needs, from the profile just created.
      const member = await Members.getByUserId(newUserId);
      const memberID = member?.memberID;
      if (!memberID) throw new Error('The account was created but no member profile was found for it.');

      // 3. The record that puts them on the list.
      if (kind === 'leader') {
        await Leaders.create({ userId: newUserId, memberID, typeID: Number(kindID) });
      } else {
        await Facilitators.create({ userId: newUserId, memberID, statusID: Number(kindID) });
      }

      toast.success(
        'Account created',
        reconnected
          ? `${firstName} now has a user account, is on the ${copy.noun} list, and their existing work is reconnected.`
          : `${firstName} now has a user account and is on the ${copy.noun} list. The account got a new id, so the existing assignment still points at the old one.`,
      );
      setOpen(false);
      onCreated?.();
    } catch (e) {
      console.error('[UnlistedAssigneeCard] create failed:', e);
      toast.error('Could not create the account', e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const displayName = fallbackName?.trim() || 'Name not on record';

  return (
    <>
      <Card withBorder radius="md" padding="lg">
        <Stack gap="md">
          <Group gap="sm" align="center">
            <Title order={3}>{displayName}</Title>
            <Badge color="orange" variant="light">No user account</Badge>
          </Group>

          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="orange"
            variant="light"
            title={`Not on the ${copy.noun} list`}
          >
            <Stack gap="xs">
              <Text size="sm">
                This person {copy.assignedTo}, but there is no user in Identity for them and so no{' '}
                {copy.noun} record either. That is why none of the tabs have anything to show.
              </Text>
              <Text size="sm">
                Creating the account below also adds the {copy.noun} record, so they appear on the list
                from then on.
              </Text>
            </Stack>
          </Alert>

          <div>
            <Text size="xs" c="dimmed" mb={4}>Referenced by the assignment as</Text>
            <Code>{userId}</Code>
          </div>

          <Divider />

          <Group>
            <Button leftSection={<IconUserPlus size={16} />} onClick={() => setOpen(true)}>
              Create user account
            </Button>
          </Group>

          <Text size="xs" c="dimmed">
            The account is created at the id above, so the case or registration that referenced this
            person reconnects to it.
          </Text>
        </Stack>
      </Card>

      <Drawer
        opened={open}
        onClose={() => setOpen(false)}
        title="Create user account"
        position="right"
        size="md"
        padding="md"
      >
        <Stack>
          <Text size="sm" c="dimmed">
            Seeded from the name on the assignment. Correct anything that is wrong before saving.
          </Text>

          <TextInput
            label="First name"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.currentTarget.value)}
          />
          <TextInput
            label="Surname"
            value={surname}
            onChange={(e) => setSurname(e.currentTarget.value)}
          />
          <TextInput
            label="Email"
            required
            type="email"
            description="Used as the sign-in address."
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          <TextInput
            label="Cellphone"
            value={cellphone}
            onChange={(e) => setCellphone(e.currentTarget.value)}
          />
          <Select
            label={kind === 'leader' ? 'Leader type' : 'Facilitator status'}
            placeholder={kind === 'leader' ? 'Select a type' : 'Select a status'}
            required
            data={lookupOptions}
            value={kindID}
            onChange={setKindID}
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={saving} disabled={!canSave}>
              Create
            </Button>
          </Group>
        </Stack>
      </Drawer>
    </>
  );
}
